import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Send, User, AlertCircle, Check, Sparkles } from 'lucide-react';
import { theme } from '../styles/theme';
import AIIcon from './AIIcon';
import { REFINE_PROMPT_PRESETS } from '../utils/refinementUtils';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing[4]};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
  border-bottom: 1px solid ${theme.colors.border};
  padding: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.md};
  color: white;
`;

const HeaderText = styled.div`
  h2 {
    margin: 0 0 ${theme.spacing[1]} 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.xl};
  }

  p {
    margin: 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.error[100]};
    color: ${theme.colors.error[600]};
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing[6]};
`;

const QuestionSection = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const QuestionLabel = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: ${theme.spacing[2]};
`;

const QuestionText = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const CurrentResponseSection = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${theme.spacing[3]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const StreamingResponseSection = styled.div<{ isVisible: boolean }>`
  margin-bottom: ${theme.spacing[6]};
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  max-height: ${({ isVisible }) => isVisible ? '300px' : '0'};
  overflow: hidden;
  transition: all ${theme.transitions.base};
`;

const StreamingContainer = styled.div<{ isStreaming: boolean }>`
  background: ${theme.colors.surface};
  border: 2px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};
  max-height: 200px;
  overflow-y: auto;
  position: relative;
  white-space: pre-wrap;

  ${({ isStreaming }) => isStreaming && `
    &::after {
      content: '';
      display: inline-block;
      width: 2px;
      height: 16px;
      background: ${theme.colors.primary[500]};
      animation: blink 1s infinite;
      margin-left: 2px;
    }

    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0; }
    }
  `}

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.backgroundSecondary};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary[400]};
    border-radius: ${theme.borderRadius.full};
  }
`;

const ResponseDisplay = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.textPrimary};
  max-height: 150px;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.backgroundSecondary};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary[400]};
    border-radius: ${theme.borderRadius.full};
  }
`;

const RefineSection = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const PresetStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin: ${theme.spacing[3]} 0 ${theme.spacing[3]};
`;

const PresetLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: ${theme.typography.fontWeight.semibold};
  margin-top: ${theme.spacing[2]};
`;

const PresetChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.full};
  color: ${theme.colors.primary[700]};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[400]};
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  resize: vertical;
  font-family: inherit;
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const HelperText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  line-height: ${theme.typography.lineHeight.relaxed};
  color: ${theme.colors.primary[700]};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.backgroundSecondary};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border: 1px solid ${({ variant }) => 
    variant === 'primary' ? theme.colors.primary[500] : theme.colors.border
  };
  background: ${({ variant }) => 
    variant === 'primary' ? theme.colors.primary[500] : 'transparent'
  };
  color: ${({ variant }) => 
    variant === 'primary' ? 'white' : theme.colors.textSecondary
  };
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  min-width: 120px;

  &:hover {
    background: ${({ variant }) => 
      variant === 'primary' 
        ? theme.colors.primary[600] 
        : theme.colors.backgroundSecondary
    };
    color: ${({ variant }) => 
      variant === 'primary' ? 'white' : theme.colors.textPrimary
    };
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CharacterCount = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  text-align: right;
  margin-top: ${theme.spacing[1]};
`;

interface HumanRefinementModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: string;
  currentResponse: string;
  onRefine: (refinementPrompt: string, callbacks: {
    onChunk: (chunk: string) => void;
    onComplete: (finalResponse: string) => void;
    onError: (error: Error) => void;
  }) => void;
  onAcceptRefinement?: (refinedResponse: string) => void;
  isLoading?: boolean;
}

const HumanRefinementModal: React.FC<HumanRefinementModalProps> = ({
  isOpen,
  onClose,
  question,
  currentResponse,
  onRefine,
  onAcceptRefinement,
  isLoading = false
}) => {
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSubmit = () => {
    if (refinementPrompt.trim()) {
      setIsStreaming(true);
      setStreamingResponse('');
      
      onRefine(refinementPrompt.trim(), {
        onChunk: (chunk: string) => {
          setStreamingResponse(prev => prev + chunk);
        },
        onComplete: (finalResponse: string) => {
          setIsStreaming(false);
          setStreamingResponse(finalResponse);
          setShowConfirmation(true);
        },
        onError: (error: Error) => {
          setIsStreaming(false);
          setStreamingResponse(`Error: ${error.message}`);
        }
      });
    }
  };

  const handleClose = () => {
    setRefinementPrompt('');
    setStreamingResponse('');
    setIsStreaming(false);
    setShowConfirmation(false);
    onClose();
  };

  const handleAcceptRefinement = () => {
    if (onAcceptRefinement && streamingResponse) {
      onAcceptRefinement(streamingResponse);
    }
    handleClose();
  };

  const handleRejectRefinement = () => {
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const maxChars = 1000;
  const remainingChars = maxChars - refinementPrompt.length;

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleClose} onKeyDown={handleKeyDown}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderContent>
            <HeaderLeft>
              <HeaderIcon>
                <User size={24} />
              </HeaderIcon>
              <HeaderText>
                <h2>Human-in-the-Loop Refinement</h2>
                <p>Provide specific instructions to improve this response</p>
              </HeaderText>
            </HeaderLeft>
            <CloseButton onClick={handleClose}>
              <X size={20} />
            </CloseButton>
          </HeaderContent>
        </ModalHeader>

        <ModalBody>
          <QuestionSection>
            <QuestionLabel>Question:</QuestionLabel>
            <QuestionText>{question}</QuestionText>
          </QuestionSection>

          <CurrentResponseSection>
            <SectionTitle>
              <AIIcon size={16} />
              Current AI Response
            </SectionTitle>
            <ResponseDisplay>{currentResponse}</ResponseDisplay>
          </CurrentResponseSection>

          <StreamingResponseSection isVisible={isStreaming || !!streamingResponse}>
            <SectionTitle>
              <AIIcon size={16} />
              AI Refined Response
            </SectionTitle>
            <StreamingContainer isStreaming={isStreaming}>{streamingResponse}</StreamingContainer>
          </StreamingResponseSection>

          {!showConfirmation && (
            <RefineSection>
              <SectionTitle>
                <User size={16} />
                Your Refinement Instructions
              </SectionTitle>

              {/* Quick-action preset chips. Click → fills the textarea with a
                  bid-writer-tested directive. Covers ~80% of refinement intents
                  so the user rarely needs to compose from scratch. */}
              <PresetLabel>
                <Sparkles size={12} /> Quick refinements
              </PresetLabel>
              <PresetStrip>
                {REFINE_PROMPT_PRESETS.map((p) => (
                  <PresetChip
                    key={p.id}
                    type="button"
                    disabled={isLoading}
                    onClick={() => setRefinementPrompt(p.prompt)}
                    title={p.prompt}
                  >
                    <Sparkles size={11} />
                    {p.label}
                  </PresetChip>
                ))}
              </PresetStrip>

              <TextArea
                value={refinementPrompt}
                onChange={(e) => setRefinementPrompt(e.target.value)}
                placeholder="Pick a quick refinement above or describe how you'd like to improve this response — for example: make it more specific to our company's experience, add a case study, adjust tone to be more confident, or address a specific evaluation criterion."
                maxLength={maxChars}
                disabled={isLoading}
              />
              <CharacterCount>
                {remainingChars} characters remaining
              </CharacterCount>
              
              <HelperText>
                <AlertCircle size={14} />
                <div>
                  <strong>Tip:</strong> Be specific about what you want to change. 
                  The AI will use your instructions to create a more targeted and effective response 
                  while maintaining professional quality and coherence.
                </div>
              </HelperText>
            </RefineSection>
          )}
          
          {showConfirmation && (
            <RefineSection>
              <SectionTitle>
                <User size={16} />
                Review Refined Response
              </SectionTitle>
              <HelperText>
                <AlertCircle size={14} />
                <div>
                  <strong>Review:</strong> Compare the original and refined responses above. 
                  Accept the refinement to apply the changes, or reject to keep the original response.
                </div>
              </HelperText>
            </RefineSection>
          )}
        </ModalBody>

        <ModalFooter>
          <div />
          {showConfirmation ? (
            <ButtonGroup>
              <Button variant="secondary" onClick={handleRejectRefinement}>
                Reject
              </Button>
              <Button variant="primary" onClick={handleAcceptRefinement}>
                <Check size={16} />
                Accept Refinement
              </Button>
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSubmit} 
                disabled={!refinementPrompt.trim() || isLoading || isStreaming}
              >
                <Send size={16} />
                {isLoading || isStreaming ? 'Refining...' : 'Refine Response'}
              </Button>
            </ButtonGroup>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default HumanRefinementModal;